import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { getWalletState } from '@/services/walletService';
import { clearSwrCache } from '@/lib/swrCache';
import type { Advertiser } from '../../../shared/types/advertiser';

interface AdvertiserUser {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'owner' | 'admin' | 'member' | 'viewer' | 'advertiser';
  isAdvertiser: boolean;
  advertiserId?: string;
}

interface AuthContextType {
  user: FirebaseUser | null;
  userData: AdvertiserUser | null;
  advertiser: Advertiser | null;
  loading: boolean;
  isAdvertiser: boolean;
  isSuperAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<AdvertiserUser | null>(null);
  const [advertiser, setAdvertiser] = useState<Advertiser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = async (firebaseUser: FirebaseUser) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (!userDoc.exists()) {
        setUserData(null);
        setAdvertiser(null);
        return;
      }
      const data = userDoc.data();
      const next: AdvertiserUser = {
        id: userDoc.id,
        email: data.email || firebaseUser.email || '',
        name: data.name || '',
        role: data.role || 'member',
        isAdvertiser: data.isAdvertiser || false,
        advertiserId: data.advertiserId,
      };
      setUserData(next);

      // Gate routes off the Firestore advertisers mirror doc (~100ms) instead of
      // the getAdvertiserWalletState callable: that Cloud Function cold-starts in
      // 5-6s and was blocking first paint of every page. The mirror is rewritten
      // by every status-changing callable (syncAdvertiserAccessDocs), and the one
      // status it can miss (SQL-side auto-pause on wallet depletion) does not
      // change route gating — 'paused' advertisers keep dashboard access.
      if (next.isAdvertiser && next.advertiserId) {
        try {
          const advDoc = await getDoc(doc(db, 'advertisers', next.advertiserId));
          if (advDoc.exists()) {
            setAdvertiser({ id: advDoc.id, ...advDoc.data() } as Advertiser);
            return;
          }
        } catch (mirrorErr) {
          console.error('Error fetching advertiser mirror doc, falling back to Cloud Function:', mirrorErr);
        }
        // Mirror missing (legacy account) or unreadable — fall back to the
        // authoritative Supabase row via the callable.
        try {
          const walletState = await getWalletState(next.advertiserId, 1);
          if (walletState.advertiser) {
            setAdvertiser(walletState.advertiser);
            return;
          }
        } catch (advErr) {
          console.error('Error fetching advertiser from Supabase:', advErr);
        }
        setAdvertiser(null);
      } else {
        setAdvertiser(null);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setUserData(null);
      setAdvertiser(null);
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) await fetchUserData(firebaseUser);
      else {
        setUserData(null);
        setAdvertiser(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signOut = async () => {
    clearSwrCache();
    await firebaseSignOut(auth);
    setUserData(null);
    setAdvertiser(null);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const refreshUserData = async () => {
    if (user) await fetchUserData(user);
  };

  const isAdvertiser = userData?.isAdvertiser || false;
  const isSuperAdmin = userData?.role === 'super_admin';

  return (
    <AuthContext.Provider
      value={{
        user,
        userData,
        advertiser,
        loading,
        isAdvertiser,
        isSuperAdmin,
        signIn,
        signOut,
        resetPassword,
        refreshUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
